import { ReactElement, useMemo, useEffect, useCallback, memo } from 'react';
import { useFormikContext } from 'formik';
import { compact } from 'lodash';

import { PlanFeaturesTypes } from '~globals/types/enums';
import { useSubscriptionFeatures } from '~hooks/useSubscriptionFeatures';
import { useToggle } from '~hooks/useToggle';
import AccessFeature from '~components/AccessFeature';
import { hasError } from '~utils/formHelpers';

import {
  SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME,
  getScheduleRouteBaseFormInititalValues,
} from '../../utils/scheduleRouteForm';

import AssistantGenerateRoutesDialogCollapsableCard from '../CollapsableCard';

import { AssistantGenerateRoutesDialogScheduleRouteCardProps } from './types';
import AssistantGenerateRoutesDialogScheduleRouteCardTitle from './Title';
import AssistantGenerateRoutesDialogScheduleRouteCardIndicator from './Indicator';
import AssistantGenerateRoutesDialogScheduleRouteCardForm from './Form';

const AssistantGenerateRoutesDialogScheduleRouteCard = ({
  onEnabled,
  startDate,
  infoText,
  withPreAssignedDriver = false,
  currentPreAssignedDriver,
  onAssignedDriver,
}: AssistantGenerateRoutesDialogScheduleRouteCardProps): ReactElement => {
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const { touched, errors, submitCount } =
    useFormikContext<
      ReturnType<typeof getScheduleRouteBaseFormInititalValues>
    >();

  const hasAccesMgmRepeatRoutes = useMemo(
    () => hasAccessToFeature(PlanFeaturesTypes.MgmRepeatRoutes),
    [hasAccessToFeature],
  );

  const [expanded, toggleExpanded] = useToggle(false);

  const handleEnabled = useCallback(
    (isEnabled: boolean) => {
      toggleExpanded(isEnabled);
      onEnabled(isEnabled);
    },
    [onEnabled, toggleExpanded],
  );

  const hasErrorsSection = useMemo(() => {
    const errorsSection = [
      hasError(
        touched,
        errors,
        SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_TYPE,
      ),
      hasError(
        touched,
        errors,
        SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_INTERVAL_COUNT,
      ),
      hasError(
        touched,
        errors,
        SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_END_DATE,
      ),
      hasError(
        touched,
        errors,
        SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_REPETION_INTERVAL_DAYS,
      ),
    ];

    return compact(errorsSection).length > 0;
  }, [errors, touched]);

  // It expands when a new submit is detected and there are errors
  useEffect(() => {
    if (hasErrorsSection) {
      toggleExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  return (
    <AccessFeature.Badge
      validFeature={PlanFeaturesTypes.MgmRepeatRoutes}
      fullWidth
      openAccessFeatureModalOnClickChip
      showChip
    >
      <AssistantGenerateRoutesDialogCollapsableCard
        expanded={expanded}
        onExpanded={() => toggleExpanded()}
        title={
          <AssistantGenerateRoutesDialogScheduleRouteCardTitle
            onEnabled={handleEnabled}
            isExpandedCard={expanded}
          />
        }
        indicator={<AssistantGenerateRoutesDialogScheduleRouteCardIndicator />}
        disabled={!hasAccesMgmRepeatRoutes}
      >
        <AssistantGenerateRoutesDialogScheduleRouteCardForm
          startDate={startDate}
          infoText={infoText}
          withPreAssignedDriver={withPreAssignedDriver}
          currentPreAssignedDriver={currentPreAssignedDriver}
          onAssignedDriver={onAssignedDriver}
        />
      </AssistantGenerateRoutesDialogCollapsableCard>
    </AccessFeature.Badge>
  );
};

export default memo(AssistantGenerateRoutesDialogScheduleRouteCard);
